import { Modal, Spin, Flex } from "antd";
import { useTranslate } from "@refinedev/core";
import { useEffect, useRef } from "react";
import { CircleNotchIcon } from "@phosphor-icons/react";
import useInitSubscription from "pages/pricing/hooks/useInitSubscription";
import { useOrganization } from "hooks/useOrganization";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { useStripePricingScript } from "@/hooks/useStripePricingScript";

interface PricingModalProps {
  open: boolean;
  onClose: () => void;
}

const PricingModal = ({ open, onClose }: PricingModalProps) => {
  const { trackEvent } = useAnalytics();
  const t = useTranslate();
  const { organization } = useOrganization({});
  const { params, isLoading, init } = useInitSubscription({
    organizationId: organization?.id ?? "",
  });
  const hasInitialized = useRef(false);
  const pricingTableId = import.meta.env.VITE_STRIPE_PRICING_TABLE_ID;
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  useStripePricingScript(open);

  // Initialize subscription params when modal opens
  useEffect(() => {
    if (open && organization?.id && !hasInitialized.current) {
      hasInitialized.current = true;
      init();
      trackEvent(AnalyticsEvents.PricingView, {
        container: "pricing_modal",
        pricing_table_id: pricingTableId,
      });
    }
    if (!open) {
      hasInitialized.current = false;
    }
  }, [open, organization?.id, init, trackEvent, pricingTableId]);

  const modalBackground = "rgb(248, 248, 248)";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1200}
      footer={null}
      title={t("pages.subscription.pricingModal.title")}
      destroyOnHidden
      styles={{
        content: { background: modalBackground },
        body: { background: modalBackground },
        header: { background: modalBackground },
      }}
    >
      {isLoading || !params ? (
        <Flex
          justify="center"
          align="center"
          style={{ minHeight: 400, background: modalBackground }}
        >
          <Spin indicator={<CircleNotchIcon style={{ fontSize: 48 }} />} />
        </Flex>
      ) : (
        <stripe-pricing-table
          pricing-table-id={pricingTableId}
          publishable-key={publishableKey}
          customer-session-client-secret={
            params.customer_session_client_secret ?? undefined
          }
          client-reference-id={params.client_reference_id}
          customer-email={params.customer_email}
        />
      )}
    </Modal>
  );
};

export default PricingModal;
